<template>
  <div class="line-chart">
    <canvas ref="lineChart"></canvas>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import { getDecimalNumberString } from "@/utils"; // Import the utility function

export default {
  name: "BarChart",
  props: {
    data: Object, // Accepts an object containing labels and datasets for the chart
    unit: {
      default: "",
    },
    aspectRatio: {
      default: 3
    },
    hideLegend: {
      default: false
    },
    fraction: {
      default: 2,
    },
    chartHeight: {
      type: String,
    },
    chartType: {
      default: "bar"
    },
    /** Gestapelte Achsen (Kosten-Stack + Umsatz nebeneinander) */
    stacked: {
      type: Boolean,
      default: false,
    },
    /** Ein Tooltip für alle Serien am selben X (Crosshair-Verhalten) */
    indexTooltip: {
      type: Boolean,
      default: false,
    },
    /** Optionale Fußzeilen im Tooltip: (items) => string[] */
    tooltipFooter: {
      type: Function,
      default: null,
    },
    /** Dataset-Labels, die ausgeblendet werden (Gruppen-Steuerung) */
    hiddenDatasets: {
      type: Array,
      default: () => [],
    },
  },
  mounted() {
    this.renderLineChart();
  },
  watch: {
    // Daten kommen asynchron / ändern sich bei Zeitraumwechsel — neu aufbauen
    data: {
      deep: true,
      handler() {
        this.renderLineChart();
      },
    },
    hiddenDatasets() {
      this.renderLineChart();
    },
  },
  beforeDestroy() {
    if (this._chart) this._chart.destroy();
  },
  methods: {
    renderLineChart() {
      const ctx = this.$refs.lineChart.getContext("2d");
      if (this._chart) {
        this._chart.destroy();
        this._chart = null;
      }

      const chartData = {
        ...this.data,
        datasets: (this.data?.datasets || []).map((dataset) => ({
          ...dataset,
          hidden: this.hiddenDatasets.includes(dataset.label),
        })),
      };
      const chart = new Chart(ctx, {
        type: this.chartType,
        data: chartData,
        options: {
          responsive: true,
          aspectRatio: this.aspectRatio,
          interaction: this.indexTooltip
            ? { mode: "index", intersect: false }
            : undefined,
          scales: this.stacked
            ? {
                x: { stacked: true, grid: { display: false } },
                y: { stacked: true, grid: { color: "rgba(0,0,0,0.06)" } },
              }
            : undefined,
          plugins: {
            legend: {
              display: !this.hideLegend, // Hide the legend for a cleaner display
              labels: { boxWidth: 14, boxHeight: 10, font: { size: 11 } },
            },
            tooltip: {
              mode: this.indexTooltip ? "index" : undefined,
              intersect: this.indexTooltip ? false : undefined,
              itemSort: (a, b) => (b.parsed?.y || 0) - (a.parsed?.y || 0),
              callbacks: {
                label: (context) => {
                  const label = context.dataset?.label || context.label || "";
                  const value = context.parsed?.y ?? context.parsed ?? 0;
                  return `${label}: ${getDecimalNumberString(value, this.fraction)} ${this.unit}`;
                },
                footer: (items) =>
                  this.tooltipFooter ? this.tooltipFooter(items) : undefined,
              },
            },
          },
        },
      });
      this._chart = chart;
    },
  },
};
</script>

<style lang="scss" scoped>
.line-chart {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;

  .title {
    font-size: 24px;
    text-transform: uppercase;
  }
}

/* Additional styles similar to your bar chart can be added here */
</style>
